import type { EaField, EaFieldPart, EaSectionDef, EaValues } from "./types";

/**
 * Abschnitts- und Feldstruktur der Ertragsausfall-/BU-Vertragsmaske.
 *
 * Abgebildet ist die Felddefinition „Ertragsausfall Vertragsmaske f. separate
 * Deckung.xlsx“, Blatt „Vertragsmaske“ (Stand 26.08.2026), mit ihren acht
 * Abschnitten:
 *
 *   1. Grunddaten / Vertragsart
 *   2. Versicherungssumme und Bemessungsgrundlage
 *   3. Haftzeit / Selbstbehalt
 *   4. Versicherte Gefahren – KBU-Status
 *   5. Besondere Ertragsausfalldeckungen
 *   6. Klauseln, Ausschlüsse und besondere Vereinbarungen
 *   7. Dokumentation / Nachweise
 *   8. Fachlicher Soll-Ist-Abgleich
 *
 * Umfangreiche Abschnitte sind in mehrere Schritte geteilt, damit ein Schritt
 * auf einen Bildschirm passt — die Abschnittsgrenzen der Excel bleiben dabei
 * unverändert.
 */

/** Deckung besteht — steuert die Sichtbarkeit fast aller Folgefelder. */
const hasDeckung = (v: EaValues) => v["deckung_vorhanden"] !== "nein";

/** Standard-Teilfelder eines Deckungsbausteins aus Abschnitt 5. */
const LIMIT_EUR: EaFieldPart = { key: "limit_eur", label: "Limit (EUR)", type: "eur" };
const LIMIT_PCT: EaFieldPart = { key: "limit_pct", label: "Limit (%)", type: "percent" };
const HAFTZEIT: EaFieldPart = { key: "haftzeit", label: "Haftzeit (Monate)", type: "months" };
const KARENZ: EaFieldPart = { key: "karenz", label: "Karenzzeit", type: "text", placeholder: "z. B. 24 Stunden" };

export const EA_CONTRACT_SECTIONS: EaSectionDef[] = [
  // ── 1. Grunddaten / Vertragsart ─────────────────────────────────────────
  {
    key: "grunddaten",
    title: "Grunddaten / Vertragsart",
    shortTitle: "Grunddaten",
    color: "purple",
    steps: [
      {
        kind: "grunddaten",
        key: "grunddaten",
        title: "Versicherungsnehmer & Deckungsart",
        description:
          "Für welchen Kontakt wird der Vertrag erfasst, besteht überhaupt eine Ertragsausfalldeckung und in welcher Form ist sie abgeschlossen?",
        fields: [
          {
            key: "deckung_vorhanden",
            label: "Betriebsunterbrechungs-/Ertragsausfalldeckung vorhanden",
            type: "yesno",
            source: "Courté-Feld / Vertragsprüfung",
            check: "Wenn Nein und Soll-Bedarf = Ja → Deckungslücke",
            requirement: "Pflicht",
            span: 2,
          },
          {
            key: "deckungsart",
            label: "Deckungsart",
            type: "select",
            options: [
              "KBU gekoppelt an Inhaltsversicherung",
              "separate Ertragsausfall-BU",
              "Elektronik-BU",
              "Maschinen-BU",
              "sonstige",
            ],
            source: "Courté-Feld; KBU-Status je Gefahr aus *_O",
            check: "Mit Soll-Bedarf aus Risikoanalyse vergleichen",
            requirement: "Pflicht bei Deckung",
            span: 2,
            showWhen: hasDeckung,
          },
          {
            key: "versicherer",
            label: "Versicherer",
            type: "text",
            placeholder: "z. B. Generali",
            source: "Vertragsstammdaten",
            showWhen: hasDeckung,
          },
          {
            key: "vertragsnummer",
            label: "Vertragsnummer",
            type: "text",
            source: "Vertragsstammdaten",
            hint: "Bleibt das Feld leer, vergibt das System eine vorläufige Nummer.",
            showWhen: hasDeckung,
          },
          {
            key: "versicherungsnummer",
            label: "Versicherungsnummer",
            type: "text",
            source: "Vertragsstammdaten",
            showWhen: hasDeckung,
          },
          {
            key: "versicherungsbeginn",
            label: "Versicherungsbeginn",
            type: "date",
            source: "Vertragsstammdaten",
            showWhen: hasDeckung,
          },
        ],
      },
      {
        kind: "umfeld",
        key: "umfeld",
        title: "Versicherte Unternehmen & Betriebsstätten",
        description:
          "Wer ist mitversichert und welche Risikoorte deckt der Vertrag ab? Beides wird verknüpft statt doppelt erfasst.",
        fields: [
          {
            key: "versicherte_unternehmen",
            label: "Versicherte Unternehmen / Mitversicherte",
            type: "tags",
            source: "Vertragsstammdaten",
            check: "Unternehmensverbund / Wechselwirkungsrisiko beachten",
            requirement: "Verknüpfung statt Doppeleingabe",
            hint: "Mit Enter bestätigen. Der Versicherungsnehmer selbst muss nicht erfasst werden.",
            span: 2,
          },
          // „Versicherte Betriebsstätten / Risikoorte“ wird als Mehrfachauswahl
          // der Risikoobjekte des Kontakts gerendert (eigene Darstellung).
        ],
      },
    ],
  },

  // ── 2. Versicherungssumme und Bemessungsgrundlage ────────────────────────
  {
    key: "summen",
    title: "Versicherungssumme und Bemessungsgrundlage",
    shortTitle: "Summen",
    color: "green",
    steps: [
      {
        kind: "bemessung",
        key: "bemessung",
        title: "Versicherungssumme & Bemessungsgrundlage",
        description:
          "Auf welcher Grundlage wurde die Versicherungssumme ermittelt — und mit welchen Werten?",
        fields: [
          {
            key: "vs_kbu",
            label: "Versicherungssumme KBU / Ertragsausfall",
            type: "eur",
            source: "VSUMKBU_O soweit vorhanden; sonst Courté-Feld",
            check: "Vergleich mit Soll Versicherungsrohertrag / vereinbarter Summenlogik",
            requirement: "Pflicht",
          },
          {
            key: "bemessungsgrundlage",
            label: "Bemessungsgrundlage",
            type: "select",
            options: [
              "Versicherungsrohertrag",
              "Deckungsbeitrag",
              "Umsatz",
              "feste Summe",
              "sonstige",
            ],
            source: "Courté-Feld; bestehend bisher nur als Ermittlungsmethode angedeutet",
            check: "Muss fachlich zur Risikoanalyse passen",
            requirement: "Pflicht",
          },
          {
            key: "jahresrohertrag",
            label: "Versicherter Jahresrohertrag / versicherte Jahresleistung",
            type: "eur",
            source: "Neues Courté-Feld",
            check: "Direkter Soll-Ist-Abgleich mit Risikoanalyse",
            requirement: "Pflicht, wenn Rohertragsbasis",
          },
          {
            key: "jahresumsatz",
            label: "Zugrunde gelegter Jahresumsatz",
            type: "eur",
            source: "Neues Courté-Feld / ggf. Kennzahlenreferenz",
            check: "Plausibilisierung, nicht als alleiniger Ersatz für Versicherungsrohertrag",
            requirement: "Optional je Summenart",
          },
          {
            key: "nicht_fortlaufende_kosten",
            label: "Abgezogene nicht fortlaufende Kosten",
            type: "composite",
            parts: [
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "pct", label: "Anteil (%)", type: "percent" },
              { key: "beschreibung", label: "Beschreibung", type: "text", span: 2 },
            ],
            source: "Neues Courté-Feld",
            check: "Dokumentiert Ermittlung des Versicherungsrohertrags",
            requirement: "Optional, aber empfohlen",
            span: 2,
          },
          {
            key: "bemessungsjahr",
            label: "Bemessungsjahr / Stichtag",
            type: "composite",
            parts: [
              { key: "jahr", label: "Geschäftsjahr", type: "number", placeholder: "z. B. 2025" },
              { key: "stichtag", label: "Stichtag", type: "date" },
            ],
            source: "Neues Courté-Feld",
            check: "Warnung bei veralteter Summenermittlung",
            requirement: "Empfohlen",
            span: 2,
          },
          {
            key: "ermittlung_vs",
            label: "Ermittlung der Versicherungssumme",
            type: "select",
            options: [
              "Kundenangabe",
              "Versichererberechnung",
              "Steuerberater",
              "Courté-Ermittlung",
              "Gutachten",
              "sonstige",
            ],
            source: "Bestehendes fachliches Feld",
            check: "Nachvollziehbarkeit der Summenfestsetzung",
            requirement: "Pflicht",
            span: 2,
          },
        ],
      },
      {
        kind: "limits",
        key: "limits",
        title: "Vorsorge, Anpassung & Entschädigungsgrenzen",
        description:
          "Zuschläge und Anpassungsregeln auf der einen, wirksame Entschädigungsgrenzen auf der anderen Seite.",
        fields: [
          {
            key: "vorsorge",
            label: "Vorsorge / Summenzuschlag",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT],
            source: "Courté-Feld / Police",
            check: "Wachstum und Saisonalität aus Analyse berücksichtigen",
            requirement: "Falls vereinbart",
            span: 2,
          },
          {
            key: "summenanpassung",
            label: "Summenanpassung / Nachhaftung",
            type: "coverage",
            parts: [{ key: "regelung", label: "Regelung", type: "text", span: 2 }],
            source: "Courté-Feld / Police",
            check: "Dynamik bei wachsendem Betrieb prüfen",
            requirement: "Falls vereinbart",
            span: 2,
          },
          {
            key: "uvv",
            label: "Unterversicherungsverzicht",
            type: "coverage",
            parts: [{ key: "grundlage", label: "Grundlage", type: "text", span: 2 }],
            source: "Courté-Feld / Police",
            check: "Nicht mit korrekter Summenermittlung verwechseln",
            requirement: "Falls vereinbart",
            span: 2,
          },
          {
            key: "hoechstentschaedigung",
            label: "Höchstentschädigung",
            type: "composite",
            parts: [LIMIT_EUR, LIMIT_PCT],
            source: "LMT_BTRG / LIMITPRZ soweit anwendbar",
            check: "Sollwert und Versicherungssumme gegen Limit prüfen",
            requirement: "Falls vorhanden",
          },
          {
            key: "jahreshoechstentschaedigung",
            label: "Jahreshöchstentschädigung",
            type: "composite",
            parts: [LIMIT_EUR, LIMIT_PCT],
            source: "JHE soweit vorhanden",
            check: "Kann effektive Deckung trotz ausreichender VS begrenzen",
            requirement: "Falls vorhanden",
          },
        ],
      },
    ],
  },

  // ── 3. Haftzeit / Selbstbehalt ──────────────────────────────────────────
  {
    key: "haftzeit",
    title: "Haftzeit / Selbstbehalt",
    shortTitle: "Haftzeit & SB",
    color: "blue",
    steps: [
      {
        kind: "haftzeit",
        key: "haftzeit",
        title: "Haftzeit & Selbstbehalt",
        description:
          "Wie lange leistet der Versicherer — und ab wann? Beides entscheidet über die tatsächliche Wirkung der Deckung.",
        fields: [
          {
            key: "haftzeit",
            label: "Haftzeit",
            type: "months",
            source: "HAFTZEIT_O",
            check: "Soll Haftzeit aus Risikoanalyse vs. Vertrags-Haftzeit",
            requirement: "Pflicht",
          },
          {
            key: "haftzeit_abweichend",
            label: "Abweichende Haftzeit je Teildeckung",
            type: "coverage",
            parts: [
              { key: "teildeckung", label: "Teildeckung", type: "text", span: 2 },
              { key: "monate", label: "Haftzeit (Monate)", type: "months" },
            ],
            source: "Courté-Feld / Police",
            check: "Bei Rückwirkung, Mehrkosten, TBU etc. separat prüfen",
            requirement: "Falls abweichend",
            span: 2,
          },
          {
            key: "karenzzeit",
            label: "Karenzzeit Ertragsausfall",
            type: "composite",
            parts: [
              { key: "wert", label: "Dauer", type: "number" },
              { key: "einheit", label: "Einheit", type: "select", options: ["Stunden", "Tage"] },
            ],
            source: "SBTAGE",
            check: "Materielle Auswirkung auf kurze Unterbrechungen",
            requirement: "Falls vereinbart",
            span: 2,
          },
          {
            key: "selbstbehalt",
            label: "Fester / prozentualer Selbstbehalt",
            type: "composite",
            parts: [
              { key: "eur", label: "Fester SB (EUR)", type: "eur" },
              { key: "pct", label: "Prozentualer SB (%)", type: "percent" },
              { key: "min", label: "Mindest-SB (EUR)", type: "eur" },
              { key: "max", label: "Höchst-SB (EUR)", type: "eur" },
            ],
            source: "SB / SBPRZ / SBMIN / SBMAX soweit vorhanden",
            check: "Effektive Eigenbeteiligung anzeigen",
            requirement: "Falls vereinbart",
            span: 2,
          },
        ],
      },
    ],
  },

  // ── 4. Versicherte Gefahren – KBU-Status ────────────────────────────────
  {
    key: "gefahren",
    title: "Versicherte Gefahren – KBU-Status",
    shortTitle: "Gefahren",
    color: "pillarOrange",
    steps: [
      {
        kind: "gefahren",
        key: "gefahren",
        title: "Versicherte Gefahren – KBU-Status",
        description:
          "Je Gefahr aus dem Sachvertrag: nicht versichert, versichert ohne KBU oder versichert mit KBU. Gefahren mit Status „ohne KBU“ erscheinen automatisch in der Liste darunter.",
        // Eigene Darstellung (Gefahrentabelle); zusätzliches Freitextfeld:
        fields: [
          {
            key: "separate_bu_gefahren",
            label: "Separate BU-Gefahren / Trigger außerhalb Sachdeckung",
            type: "textarea",
            source: "Courté-Feld / Police",
            check: "Für separate BU-Deckungen, die nicht sauber über *_O abbildbar sind",
            requirement: "Falls vorhanden",
            span: 2,
          },
        ],
      },
    ],
  },

  // ── 5. Besondere Ertragsausfalldeckungen ────────────────────────────────
  {
    key: "deckungen",
    title: "Besondere Ertragsausfalldeckungen",
    shortTitle: "Besondere Deckungen",
    color: "orange",
    steps: [
      {
        kind: "deckungen",
        key: "deckungen_mehrkosten",
        title: "Mehrkosten & Mietausfall",
        description:
          "Deckungen, die den Ertragsausfall abfedern oder Mieteinnahmen ersetzen.",
        fields: [
          {
            key: "mehrkosten",
            label: "Mehrkosten",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT, HAFTZEIT],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "zusaetzliche_mehrkosten",
            label: "Zusätzliche Mehrkosten",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT, HAFTZEIT],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "mietausfall",
            label: "Mietausfall / Mietverlust",
            type: "coverage",
            parts: [
              { key: "vs", label: "Versicherungssumme (EUR)", type: "eur" },
              LIMIT_EUR,
              HAFTZEIT,
            ],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
        ],
      },
      {
        kind: "deckungen",
        key: "deckungen_rueckwirkung",
        title: "Rückwirkung & Wechselwirkung",
        description:
          "Unterbrechungen, die nicht im eigenen Betrieb entstehen — bei Zulieferern, Abnehmern, anderen Standorten oder im Verbund.",
        fields: [
          {
            key: "rueckwirkung_zulieferer",
            label: "Rückwirkung Zulieferer",
            type: "coverage",
            parts: [
              LIMIT_EUR,
              LIMIT_PCT,
              HAFTZEIT,
              { key: "benannte", label: "Benannte Zulieferer", type: "text", span: 2 },
            ],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "rueckwirkung_abnehmer",
            label: "Rückwirkung Abnehmer",
            type: "coverage",
            parts: [
              LIMIT_EUR,
              LIMIT_PCT,
              HAFTZEIT,
              { key: "benannte", label: "Benannte Abnehmer", type: "text", span: 2 },
            ],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "wechselwirkung_betriebsstaetten",
            label: "Wechselwirkung eigene Betriebsstätten",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT, HAFTZEIT],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "abhaengigkeit_verbund",
            label: "Abhängigkeit Unternehmen im Verbund",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT, HAFTZEIT],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
        ],
      },
      {
        kind: "deckungen",
        key: "deckungen_externe",
        title: "Externe Ausfälle, Behörden & Technische BU",
        description:
          "Versorgungs- und Infrastrukturausfälle, behördliche Eingriffe sowie Elektronik- und Maschinen-BU.",
        fields: [
          {
            key: "ausfall_versorgung",
            label: "Ausfall öffentlicher Versorgung",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT, KARENZ, HAFTZEIT],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "ausfall_dienstleister",
            label: "Ausfall externer Dienstleister / Infrastruktur",
            type: "coverage",
            parts: [LIMIT_EUR, LIMIT_PCT, KARENZ, HAFTZEIT],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "zugangsbeschraenkung",
            label: "Zugangsbeschränkung / behördliche Sperrung",
            type: "coverage",
            parts: [LIMIT_EUR, { key: "dauer", label: "Dauer", type: "text", placeholder: "z. B. 6 Wochen" }],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "betriebsschliessung",
            label: "Behördliche Betriebsschließung / Infektionsschutz",
            type: "coverage",
            parts: [
              LIMIT_EUR,
              { key: "dauer", label: "Dauer", type: "text", placeholder: "z. B. 30 Tage" },
              KARENZ,
            ],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "elektronik_bu",
            label: "Elektronik-BU",
            type: "coverage",
            parts: [
              { key: "vs", label: "Versicherungssumme (EUR)", type: "eur" },
              LIMIT_EUR,
              HAFTZEIT,
              { key: "sb", label: "Selbstbehalt", type: "text" },
            ],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
          {
            key: "maschinen_bu",
            label: "Maschinen-BU",
            type: "coverage",
            parts: [
              { key: "vs", label: "Versicherungssumme (EUR)", type: "eur" },
              LIMIT_EUR,
              HAFTZEIT,
              { key: "sb", label: "Selbstbehalt", type: "text" },
            ],
            source: "Neues Courté-Feld / Police / ggf. vorhandene Risikocodes",
            check: "Direkter Abgleich mit entsprechendem Sollfeld der Risikoanalyse",
            requirement: "Nur wenn vereinbart / relevant",
            span: 2,
          },
        ],
      },
    ],
  },

  // ── 6. Klauseln, Ausschlüsse und besondere Vereinbarungen ───────────────
  {
    key: "klauseln",
    title: "Klauseln, Ausschlüsse und besondere Vereinbarungen",
    shortTitle: "Klauseln",
    color: "black",
    steps: [
      {
        kind: "klauseln",
        key: "klauseln",
        title: "Klauseln, Ausschlüsse & Obliegenheiten",
        description:
          "Was die Deckung im Kleingedruckten erweitert oder begrenzt — inklusive verdeckter Sublimits.",
        fields: [
          {
            key: "klauseln_kbu",
            label: "Klauseln KBU / Ertragsausfall",
            type: "tags",
            source: "KBU-Risikocodes / BED / Dokument",
            check: "Bestehende Klausellogik weiterverwenden",
            requirement: "Falls vorhanden",
            hint: "Klauselnummer oder -bezeichnung eingeben und mit Enter bestätigen.",
            span: 2,
          },
          {
            key: "individuelle_vereinbarungen",
            label: "Individuelle Vereinbarungen",
            type: "textarea",
            source: "Courté-Feld",
            check: "Abweichung zum Standard dokumentieren",
            requirement: "Falls vorhanden",
            span: 2,
          },
          {
            key: "ausschluesse",
            label: "Ausschlüsse",
            type: "textarea",
            source: "Courté-Feld / Police",
            check: "Im Soll-Ist-Abgleich als qualitative Warnung berücksichtigen",
            requirement: "Falls vorhanden",
            span: 2,
          },
          {
            key: "obliegenheiten",
            label: "Obliegenheiten / Auflagen",
            type: "textarea",
            source: "Courté-Feld / Police",
            check: "Kritische Auflagen sichtbar machen",
            requirement: "Falls vorhanden",
            span: 2,
          },
          {
            key: "sublimits",
            label: "Besondere Voraussetzungen / Sublimits",
            type: "textarea",
            source: "Courté-Feld / Police",
            check: "Verdeckte Limitierung der BU vermeiden",
            requirement: "Falls vorhanden",
            span: 2,
          },
        ],
      },
    ],
  },

  // ── 7. Dokumentation / Nachweise ────────────────────────────────────────
  {
    key: "dokumentation",
    title: "Dokumentation / Nachweise",
    shortTitle: "Dokumentation",
    color: "purple",
    steps: [
      {
        kind: "dokumentation",
        key: "dokumentation",
        title: "Dokumentation & Nachweise",
        description:
          "Womit ist die Summenermittlung belegt? Fehlende Nachweise erzeugen im Abgleich einen Prüfhinweis.",
        fields: [
          {
            key: "dok_summenermittlung",
            label: "Summenermittlung KBU / Ertragsausfall",
            type: "coverage",
            parts: [{ key: "datum", label: "Datum", type: "date" }],
            source: "Bereits in bestehender Vertragsmaske vorgesehen",
            check: "Bei fehlendem Nachweis Warnung / Prüfhinweis",
            requirement: "Empfohlen",
            span: 2,
          },
          {
            key: "dok_jahresabschluss",
            label: "Jahresabschluss / BWA / Kennzahlenbasis",
            type: "composite",
            parts: [
              { key: "verknuepfung", label: "Verknüpfung", type: "text", span: 2 },
              { key: "stichtag", label: "Stichtag", type: "date" },
            ],
            source: "Kennzahlen / Dokumente",
            check: "Plausibilisierung der Bemessungsgrundlage",
            requirement: "Empfohlen",
            span: 2,
          },
          {
            key: "dok_versichererberechnung",
            label: "Versichererberechnung / Summenblatt",
            type: "document",
            source: "Dokumente",
            check: "Nachvollziehbarkeit",
            requirement: "Falls vorhanden",
          },
          {
            key: "dok_sondervereinbarung",
            label: "Sondervereinbarung / Freigabe Versicherer",
            type: "document",
            source: "Dokumente",
            check: "Für Abweichungen / Sonderlimits",
            requirement: "Falls vorhanden",
          },
        ],
      },
    ],
  },

  // ── 8. Fachlicher Soll-Ist-Abgleich ─────────────────────────────────────
  {
    key: "abgleich",
    title: "Fachlicher Soll-Ist-Abgleich",
    shortTitle: "Soll-Ist-Abgleich",
    color: "green",
    steps: [
      {
        kind: "sollwerte",
        key: "sollwerte",
        title: "Sollwerte aus der Risikoanalyse",
        description:
          "Der Bedarf laut Risikoanalyse Ertragsausfall. Sobald die Analyse diese Werte strukturiert liefert, werden sie automatisch übernommen — bis dahin hier erfassbar.",
        fields: [
          {
            key: "soll_rohertrag",
            label: "Soll Versicherungsrohertrag",
            type: "eur",
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertraglicher Wert / effektive Höchstentschädigung",
            requirement: "Automatisch übernehmen",
          },
          {
            key: "soll_haftzeit",
            label: "Soll Haftzeit",
            type: "months",
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliche Haftzeit",
            requirement: "Automatisch übernehmen",
          },
          {
            key: "soll_mehrkosten",
            label: "Soll Mehrkostenlimit",
            type: "eur",
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliches Limit",
            requirement: "Automatisch übernehmen",
          },
          {
            key: "soll_mietausfall",
            label: "Soll Mietausfall",
            type: "composite",
            parts: [
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "monate", label: "Dauer (Monate)", type: "months" },
            ],
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliche Deckung",
            requirement: "Automatisch übernehmen",
          },
          {
            key: "soll_rueckwirkung",
            label: "Soll Rückwirkung Zulieferer / Abnehmer",
            type: "coverage",
            parts: [LIMIT_EUR, HAFTZEIT],
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliche Rückwirkungsdeckung",
            requirement: "Automatisch übernehmen",
            span: 2,
          },
          {
            key: "soll_wechselwirkung",
            label: "Soll Wechselwirkung",
            type: "coverage",
            parts: [LIMIT_EUR, HAFTZEIT],
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliche Wechselwirkungsdeckung",
            requirement: "Automatisch übernehmen",
            span: 2,
          },
          {
            key: "soll_tbu",
            label: "Soll Elektronik-BU / Maschinen-BU",
            type: "coverage",
            parts: [
              { key: "vs", label: "Versicherungssumme (EUR)", type: "eur" },
              HAFTZEIT,
            ],
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliche TBU-Deckung",
            requirement: "Automatisch übernehmen",
            span: 2,
          },
          {
            key: "soll_betriebsschliessung",
            label: "Soll Betriebsschließung",
            type: "coverage",
            parts: [
              LIMIT_EUR,
              { key: "dauer", label: "Dauer", type: "text", placeholder: "z. B. 30 Tage" },
            ],
            source: "Risikoanalyse Ertragsausfall",
            check: "vs. vertragliche Deckung",
            requirement: "Automatisch übernehmen",
            span: 2,
          },
        ],
      },
      {
        kind: "ergebnis",
        key: "ergebnis",
        title: "Prüfergebnis & Gesamtergebnis",
        description:
          "Die Prüfregeln aus dem Blatt „Soll-Ist-Abgleich“ laufen automatisch über die erfassten Werte. Das Gesamtergebnis bleibt eine Beraterentscheidung.",
        // Eigene Darstellung (Prüftabelle + Gesamtergebnis + Zusammenfassung).
      },
    ],
  },
];

/** Alle Felder eines Schritts, gefiltert nach `showWhen`. */
export const visibleFields = (fields: EaField[] | undefined, values: EaValues): EaField[] =>
  (fields ?? []).filter((f) => !f.showWhen || f.showWhen(values));
